import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { z } from "zod";
import { en, pt } from "zod/locales";

import { locales, setLocale } from "@recobra/i18n/runtime";

export type AppLocale = (typeof locales)[number];

export type LocaleContextValue = {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => Promise<void>;
};

export const LocaleContext = createContext<LocaleContextValue | null>(null);

export const isAppLocale = (value: string | null): value is AppLocale =>
  value !== null && (locales as readonly string[]).includes(value);

const configureZodLocale = (locale: AppLocale) => {
  z.config(locale === "pt-BR" ? pt() : en());
};

export const LocaleProvider: FC<PropsWithChildren> = ({ children }) => {
  const [locale, setLocaleState] = useState<AppLocale>(() => {
    const saved =
      typeof localStorage !== "undefined" ? localStorage.getItem("PARAGLIDE_LOCALE") : null;
    const initialLocale: AppLocale = isAppLocale(saved) ? saved : "pt-BR";

    configureZodLocale(initialLocale);
    return initialLocale;
  });

  useEffect(() => {
    configureZodLocale(locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const setAppLocale = useCallback(async (next: AppLocale) => {
    await setLocale(next, { reload: false });
    configureZodLocale(next);
    setLocaleState(next);
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale: setAppLocale }),
    [locale, setAppLocale],
  );

  return (
    <LocaleContext.Provider value={value}>
      <div key={locale} className="contents">
        {children}
      </div>
    </LocaleContext.Provider>
  );
};
